import type { DragEvent, KeyboardEvent } from 'react';
import { PHASE_OF_PLAY_META, type PhaseOfPlay } from '@4cm/core/phaseOfPlay';
import { DrillTacticsThumbnail } from './DrillTacticsThumbnail';

export interface DrillTimelineCardProps {
  index: number;
  total: number;
  name: string;
  /**
   * Stable id derived from the drill (typically a slug of the name).
   * Drives the `tactics-drill-${drillId}` persistKey used by the inline
   * tactics board + the chip's thumbnail. Omit to suppress the
   * thumbnail (legacy tests that exercise the timeline shell without
   * a per-drill tactics layout in scope).
   */
  drillId?: string;
  phase?: PhaseOfPlay | null;
  durationMins?: number | null;
  active?: boolean;
  onSelect: () => void;
  onMoveLeft?: () => void;
  onMoveRight?: () => void;
  onRemove?: () => void;
  onDragStart: (e: DragEvent<HTMLButtonElement>) => void;
  onDragOver: (e: DragEvent<HTMLButtonElement>) => void;
  onDrop: (e: DragEvent<HTMLButtonElement>) => void;
  onDragEnd: (e: DragEvent<HTMLButtonElement>) => void;
}

/**
 * One drill chip on the bottom timeline rail. Click to select for the
 * centre canvas, drag to reorder. Phase glyph + colour mirrors the
 * Calendar phase-mix vocabulary so a coach sees both axes at a glance.
 *
 * Keyboard nav (Phase E a11y):
 *   - Tab focuses the chip
 *   - Enter / Space selects it (open for fine-tune)
 *   - Arrow Left / Right swaps with neighbour
 *   - Delete / Backspace removes it from the timeline
 */
export function DrillTimelineCard({
  index,
  total,
  name,
  drillId,
  phase,
  durationMins,
  active,
  onSelect,
  onMoveLeft,
  onMoveRight,
  onRemove,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: DrillTimelineCardProps) {
  const meta = phase ? PHASE_OF_PLAY_META[phase] : null;

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    switch (e.key) {
      case 'ArrowLeft':
        if (onMoveLeft && index > 0) {
          e.preventDefault();
          onMoveLeft();
        }
        break;
      case 'ArrowRight':
        if (onMoveRight && index < total - 1) {
          e.preventDefault();
          onMoveRight();
        }
        break;
      case 'Delete':
      case 'Backspace':
        if (onRemove) {
          e.preventDefault();
          onRemove();
        }
        break;
      // Enter / Space falls through to native <button> click semantics.
      default:
        break;
    }
  };

  const phaseText = meta ? `, ${meta.label} phase` : '';
  const durationText = durationMins ? `, ${durationMins} minutes` : '';
  const ariaLabel = `Drill ${index + 1} of ${total}: ${name}${durationText}${phaseText}. Use arrow keys to reorder, Enter to select, Delete to remove.`;

  return (
    <button
      type="button"
      className="session-canvas__chip"
      data-active={active || undefined}
      data-phase={phase || undefined}
      data-testid={`drill-chip-${index}`}
      draggable
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      aria-label={ariaLabel}
      aria-pressed={active || undefined}
      style={meta ? { borderColor: meta.colorHex } : undefined}
    >
      {drillId && (
        <DrillTacticsThumbnail drillId={drillId} caption={name} />
      )}
      <span className="session-canvas__chip-row">
        <span className="session-canvas__chip-index">{index + 1}</span>
        {meta && (
          <span
            className="session-canvas__chip-glyph"
            aria-hidden="true"
            style={{ color: meta.colorHex }}
          >
            {meta.glyph}
          </span>
        )}
        <span className="session-canvas__chip-name">{name}</span>
        {durationMins ? (
          <span className="session-canvas__chip-dur">{durationMins}m</span>
        ) : null}
      </span>
    </button>
  );
}
